import { IconButton, Button, Typography, Box } from "@mui/material";
import PhoneNumberField from "@/components/override/PhoneNumberField";
import { Stack } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Add, Delete } from "@mui/icons-material";
import { useFieldArray } from "react-hook-form";
import { useFormContext } from "react-hook-form";
import { ICompanyDetailsFormValues } from "@/utils/zod/generalSettingsFormValidation";

const PhoneNumberComponent = ({onSave}: {onSave: (values: ICompanyDetailsFormValues) => void}) => {
    const formContext = useFormContext();
    const { watch, getValues } = formContext;
    const phoneFieldArray = useFieldArray({
        control: formContext.control,
        name: "phoneNumbers"
    });

    // Function to validate if a phone number is valid (10 digits)
    const isValidPhoneNumber = (phoneNumber: string | undefined): boolean => {
        if (!phoneNumber) return false;
        // Remove any non-digit characters and check if it's exactly 10 digits
        const digitsOnly = phoneNumber.replace(/\D/g, '');
        return digitsOnly.length === 10;
    };

    const handleAddPhoneNumber = () => {
        const phoneNumbers = watch("phoneNumbers");
        const lastPhoneNumber = phoneNumbers?.[phoneNumbers.length - 1];
        
        // Check if there are phone numbers and validate the last one
        if (phoneNumbers?.length > 0) {
            // If empty, show error about filling the field
            if (lastPhoneNumber === "") {
                formContext.setError(`phoneNumbers.${phoneNumbers.length - 1}`, {
                    type: "manual",
                    message: "Please fill this field before adding another phone number"
                });
                return;
            }
            
            // If not a valid 10-digit number, show validation error
            if (!isValidPhoneNumber(lastPhoneNumber)) {
                formContext.setError(`phoneNumbers.${phoneNumbers.length - 1}`, {
                    type: "manual",
                    message: "Please enter a valid 10-digit phone number"
                });
                return;
            }
            
            // Clear any errors on the last field if valid
            formContext.clearErrors(`phoneNumbers.${phoneNumbers.length - 1}`);
        }
        phoneFieldArray.append('');
    };

    // Handle removing a field when it's empty on blur
    const handleRemoveField = (index: number) => {
        // Only remove if there's more than one field
        if (phoneFieldArray.fields.length > 1) {
            phoneFieldArray.remove(index);
        }
        onSave(getValues());
    };

    return (
        <>
            {/* Phone Numbers Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 2 }}>
                <Typography variant="body1">
                    Phone Numbers
                </Typography>
                <Button
                    startIcon={<Add />}
                    onClick={handleAddPhoneNumber}
                    variant="text"
                >
                    Add Phone Number
                </Button>
            </Box>

            <Grid container spacing={2}>
                {phoneFieldArray.fields.map((field, index) => {
                    return (
                        <Grid size={{ xs: 12, sm: 6 }} key={field.id}>
                            <Stack direction="column" spacing={0}>
                                <Stack direction="row" spacing={1}>
                                    <PhoneNumberField
                                        name={`phoneNumbers[${index}]`}
                                        removeField={() => handleRemoveField(index)}
                                        size="small"
                                    />
                                    <IconButton
                                        onClick={() => handleRemoveField(index)}
                                        color="error"
                                        disabled={phoneFieldArray.fields.length === 1}
                                        sx={{
                                            height: "fit-content",
                                            paddingBlock: 2
                                        }}
                                    >
                                        <Delete />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </Grid>
                    );
                })}
            </Grid>
        </>
    );
};

export default PhoneNumberComponent;
